/** Ajustes de inventario: almacén propone (pendiente), admin aprueba/rechaza; motivo + justificación obligatoria. */
import { formatearCantidad, Money, MOTIVOS_AJUSTE, type AlmacenDTO, type SucursalDTO } from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { Plus, SlidersHorizontal, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { EstadoQuery, Vacio } from '@/componentes/estados.tsx';
import { PaginadorBusqueda, useMismaBusqueda, usePaginaBusqueda } from '@/componentes/PaginadorBusqueda.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select, Textarea } from '@/componentes/ui/input.tsx';
import { Campo, Filtro } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { TituloPagina } from '@/layout/AppLayout.tsx';
import { get, mensajeDeError } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { claves } from '@/lib/query.ts';
import { formatearFechaHora } from '@/lib/utils.ts';
import { useProductos } from '@/modulos/productos/api.ts';
import { useAjuste, useAjustes, useCrearAjuste, useResolverAjuste, useVistaLibrosAjuste } from './api.ts';
import { VistaAjuste } from './VistaAjuste.tsx';

const cant = (v: string | null) => (v === null ? '—' : formatearCantidad(Money.parse(v)));
type SucursalMia = SucursalDTO & { almacenes: AlmacenDTO[] };
const ESTADO: Record<string, { t: string; v: 'ok' | 'pendiente' | 'critico' }> = { pendiente: { t: 'Pendiente', v: 'pendiente' }, aprobado: { t: 'Aprobado', v: 'ok' }, rechazado: { t: 'Rechazado', v: 'critico' } };

export function Ajustes() {
  const { puede } = useAuth();
  const [estado, setEstado] = useState('');
  const [nuevo, setNuevo] = useState(false);
  const [detalle, setDetalle] = useState<string | null>(null);
  const lista = useAjustes({ estado: estado || undefined, limite: 50 });
  const aprueba = puede('inventario.ajustes', 'total');
  return (
    <>
      <TituloPagina
        titulo="Ajustes de inventario"
        subtitulo={aprueba ? 'Aprueba o rechaza lo propuesto por almacén; tus ajustes se aplican de inmediato.' : 'Propón ajustes con motivo y justificación; los aprueba quien autoriza ajustes.'}
        acciones={<Button onClick={() => setNuevo(true)} data-testid="btn-nuevo-ajuste"><Plus /> Nuevo ajuste</Button>}
      />
      <div className="mb-3">
        <Filtro etiqueta="Estado" className="w-44">
          <Select value={estado} onChange={(e) => setEstado(e.target.value)} className="w-44" aria-label="Estado del ajuste" data-testid="ajustes-estado">
            <option value="">Todos</option>
            <option value="pendiente">Pendientes</option>
            <option value="aprobado">Aprobados</option>
            <option value="rechazado">Rechazados</option>
          </Select>
        </Filtro>
      </div>
      <EstadoQuery cargando={lista.isLoading} error={lista.error} datos={lista.data} reintentar={() => void lista.refetch()}>
        {(d) =>
          d.datos.length === 0 ? (
            <Vacio icono={<SlidersHorizontal className="h-6 w-6" />} titulo="Sin ajustes" descripcion="Todo movimiento que no sea venta o entrega pasa por aquí con justificación." />
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Ajuste</TableHead><TableHead>Tipo · motivo</TableHead><TableHead>Almacén</TableHead><TableHead>Propuesto por</TableHead><TableHead className="text-right">Valor</TableHead><TableHead>Estado</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {d.datos.map((a) => (
                  <TableRow key={a.id} data-testid={`ajuste-${a.numero_formateado}`}>
                    <TableCell className="font-medium">{a.numero_formateado}</TableCell>
                    <TableCell>{a.tipo} · {a.motivo}</TableCell>
                    <TableCell>{a.almacen_nombre}</TableCell>
                    <TableCell>{a.creado_por_nombre}<p className="text-[11px] text-muted-foreground">{formatearFechaHora(a.creado_en)}</p></TableCell>
                    <TableCell className="text-right num">{a.valor_total === null ? '—' : `$ ${Money.parse(a.valor_total).toDisplay()}`}</TableCell>
                    <TableCell><Badge variant={ESTADO[a.estado]?.v ?? 'neutro'}>{ESTADO[a.estado]?.t ?? a.estado}</Badge></TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => setDetalle(a.id)} data-testid={`ver-ajuste-${a.numero_formateado}`}>{a.estado === 'pendiente' && aprueba ? 'Revisar' : 'Ver'}</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        }
      </EstadoQuery>
      <NuevoAjuste abierto={nuevo} cerrar={() => setNuevo(false)} />
      <DetalleAjuste id={detalle} cerrar={() => setDetalle(null)} aprueba={aprueba} />
    </>
  );
}

function NuevoAjuste({ abierto, cerrar }: { abierto: boolean; cerrar: () => void }) {
  const mia = useQuery({ queryKey: claves.sucursalMia, queryFn: () => get<SucursalMia | null>('/sucursales/mia') });
  const [almacenId, setAlmacenId] = useState('');
  const [motivo, setMotivo] = useState<(typeof MOTIVOS_AJUSTE)[number]>('Rotura');
  const [just, setJust] = useState('');
  const [q, setQ] = useState('');
  const [lineas, setLineas] = useState<{ producto_id: string; codigo: string; nombre: string; cantidad: string }[]>([]);
  // De 8 en 8 con paginador al pie del desplegable (pedido de la dueña 2026-10-20)
  const [pagina, setPagina] = usePaginaBusqueda(q);
  const productos = useProductos({ q: q || undefined, pagina, limite: 8 });
  const mismaBusqueda = useMismaBusqueda(q, !!productos.data && !productos.isPlaceholderData);
  const crear = useCrearAjuste();
  const limpiar = () => { setLineas([]); setJust(''); setQ(''); };
  // Vista previa de los libros de inventario (pedido del 2026-10-30): lleva costos, solo con ese permiso
  const veCosto = useAuth().puede('productos.costos', 'lectura');
  const lineasVista = lineas.map((l) => ({ producto_id: l.producto_id, cantidad: l.cantidad.trim() }));
  const numerosOk = lineasVista.length > 0 && lineasVista.every((l) => /^-?\d+(\.\d+)?$/.test(l.cantidad) && /[1-9]/.test(l.cantidad));
  // Lo que el servidor rechazaría por el signo (400 `inventario_inicial_entra` y `uso_interno_saca`): se dice la regla
  // en vez de una vista previa de algo que no va a pasar
  const reglaSigno = !numerosOk ? null
    : motivo === 'Inventario inicial' && lineasVista.some((l) => l.cantidad.startsWith('-')) ? 'El «Inventario inicial» solo suma existencia: escribe las cantidades en positivo.'
    : motivo === 'Uso interno' && lineasVista.some((l) => !l.cantidad.startsWith('-')) ? 'El «Uso interno» solo saca mercancía: escribe las cantidades en negativo.'
    : null;
  const vistaValida = numerosOk && !reglaSigno;
  const vista = useVistaLibrosAjuste(vistaValida ? { datos: { motivo, lineas: lineasVista } } : null, veCosto);
  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && cerrar()}>
      {/* La ventana no se desplaza entera (la lista de «Agregar producto…» flota encima y quedaría cortada): tiene la altura
          tope de toda ventana y lo que crece, los renglones y la vista previa, se desplaza dentro (pedido del 2026-10-30) */}
      <DialogContent className="flex max-w-2xl flex-col overflow-visible" data-testid="dialogo-ajuste">
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle>Nuevo ajuste de inventario</DialogTitle><AyudaVentana tema="ajustes.nuevo" /></div>
          <DialogDescription>Cantidad negativa: sale (rotura, merma, pérdida); positiva: entra. La justificación es obligatoria.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Almacén" htmlFor="aj-alm"><Select id="aj-alm" value={almacenId} onChange={(e) => setAlmacenId(e.target.value)} data-testid="ajuste-almacen"><option value="">—</option>{(mia.data?.almacenes ?? []).map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}</Select></Campo>
          <Campo etiqueta="Motivo" htmlFor="aj-mot"><Select id="aj-mot" value={motivo} onChange={(e) => setMotivo(e.target.value as typeof motivo)} data-testid="ajuste-motivo">{MOTIVOS_AJUSTE.filter((m) => m !== 'Diferencia de conteo' && m !== 'Diferencia de traslado').map((m) => <option key={m} value={m}>{m}</option>)}</Select></Campo>
        </div>
        <Campo etiqueta="Justificación" htmlFor="aj-just"><Textarea id="aj-just" rows={2} value={just} onChange={(e) => setJust(e.target.value)} placeholder="Qué pasó, cuándo, quién lo vio…" data-testid="ajuste-justificacion" /></Campo>
        <div className="relative">
          <Input placeholder="Agregar producto…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ajuste-buscar" />
          {q && productos.data && mismaBusqueda ? (
            <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border bg-popover shadow-md" data-testid="ajuste-resultados">
              {/* Con altura tope y su scroll: dentro de la ventana el paginador queda a la vista en una pantalla de 768 px */}
              <ul className="max-h-44 overflow-y-auto">
                {/* Los servicios no llevan existencia: no se ajustan (hallazgo D16) */}
                {productos.data.datos.filter((p) => p.tipo !== 'servicio').map((p) => (
                  <li key={p.id}><button className="w-full px-3 py-1.5 text-left text-sm hover:bg-accent" onClick={() => { if (!lineas.some((l) => l.producto_id === p.id)) setLineas((ls) => [...ls, { producto_id: p.id, codigo: p.codigo, nombre: p.nombre, cantidad: '-1' }]); setQ(''); }} data-testid={`ajuste-producto-${p.codigo}`}>{p.codigo} · {p.nombre}</button></li>
                ))}
              </ul>
              <PaginadorBusqueda pagina={pagina} limite={8} total={productos.data.total} onPagina={setPagina} testid="ajuste-productos" />
            </div>
          ) : null}
        </div>
        {/* Renglones y vista previa con su propio desplazamiento: la ventana no se desplaza (por la lista de «Agregar
            producto…», que flota encima de esta zona) y con varios renglones el botón quedaba fuera de una pantalla de 768 px */}
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto" data-testid="ajuste-renglones">
        {lineas.length > 0 ? (
          <Table>
            <TableHeader><TableRow><TableHead>Producto</TableHead><TableHead className="text-right">Cantidad (±)</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {lineas.map((l, i) => (
                <TableRow key={l.producto_id}>
                  <TableCell>{l.nombre} <span className="text-[11px] text-muted-foreground">{l.codigo}</span></TableCell>
                  <TableCell className="text-right"><Input inputMode="decimal" value={l.cantidad} onChange={(e) => setLineas((ls) => ls.map((x, j) => (j === i ? { ...x, cantidad: e.target.value } : x)))} className="h-8 w-24 text-right" data-testid={`ajuste-cantidad-${l.codigo}`} /></TableCell>
                  <TableCell><Button size="icon" variant="ghost" onClick={() => setLineas((ls) => ls.filter((_, j) => j !== i))} aria-label="Quitar"><Trash2 className="h-4 w-4" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
        {reglaSigno ? <p className="rounded-md border border-mg-ambar/50 bg-amber-50 px-3 py-2 text-xs text-amber-900" role="alert" data-testid="ajuste-regla-signo">{reglaSigno}</p> : null}
        {veCosto && vistaValida && vista.data ? <VistaAjuste vista={vista.data} al="registrar" /> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={cerrar}>Cancelar</Button>
          <Button disabled={!almacenId || just.trim().length < 5 || lineas.length === 0} cargando={crear.isPending} onClick={() => crear.mutate({ almacen_id: almacenId, motivo, justificacion: just, lineas: lineas.map((l) => ({ producto_id: l.producto_id, cantidad: l.cantidad.trim() })) }, { onSuccess: (a) => { toast.success(`Ajuste ${a.numero_formateado} ${a.estado === 'aprobado' ? 'aplicado' : 'propuesto (pendiente de aprobación)'}`); limpiar(); cerrar(); }, onError: (e) => toast.error('No se pudo registrar', { description: mensajeDeError(e) }) })} data-testid="ajuste-guardar">
            Registrar ajuste
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetalleAjuste({ id, cerrar, aprueba }: { id: string | null; cerrar: () => void; aprueba: boolean }) {
  const a = useAjuste(id);
  const { usuario, puede } = useAuth();
  const esDuena = usuario?.rol === 'superadmin';
  const resolver = useResolverAjuste();
  // Antes de aprobar: cómo entra a los libros de inventario (pedido del 2026-10-30); lleva costos
  const veCosto = puede('productos.costos', 'lectura');
  const vista = useVistaLibrosAjuste(id && a.data?.estado === 'pendiente' && aprueba ? { id } : null, veCosto);
  const [comentario, setComentario] = useState('');
  const decidir = (decision: 'aprobar' | 'rechazar') =>
    id && resolver.mutate({ id, decision, comentario: comentario || undefined }, { onSuccess: (r) => { toast.success(`${r.numero_formateado} ${r.estado}`); cerrar(); }, onError: (e) => toast.error('No se pudo resolver', { description: mensajeDeError(e) }) });
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-w-2xl" data-testid="dialogo-detalle-ajuste">
        {a.data ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-1"><DialogTitle>{a.data.numero_formateado} · {a.data.tipo} · {a.data.motivo} <Badge variant={ESTADO[a.data.estado]?.v ?? 'neutro'}>{ESTADO[a.data.estado]?.t}</Badge></DialogTitle><AyudaVentana tema="ajustes.revisar" /></div>
              <DialogDescription>{a.data.almacen_nombre} · propuesto por {a.data.creado_por_nombre} el {formatearFechaHora(a.data.creado_en)}{a.data.resuelto_por_nombre ? ` · resuelto por ${a.data.resuelto_por_nombre}` : ''}</DialogDescription>
            </DialogHeader>
            <p className="rounded-lg bg-muted/60 p-3 text-sm">{a.data.justificacion}</p>
            {a.data.comentario_resolucion ? <p className="text-sm text-muted-foreground">Comentario: {a.data.comentario_resolucion}</p> : null}
            <Table>
              <TableHeader><TableRow><TableHead>Producto</TableHead><TableHead className="text-right">Cantidad</TableHead><TableHead className="text-right">Costo u.</TableHead><TableHead className="text-right">Antes → después</TableHead></TableRow></TableHeader>
              <TableBody>
                {a.data.lineas.map((l) => (
                  <TableRow key={l.producto_id}>
                    <TableCell>{l.producto_nombre} <span className="text-[11px] text-muted-foreground">{l.producto_codigo}</span></TableCell>
                    <TableCell className={`text-right num ${Money.parse(l.cantidad).esNegativo() ? 'text-mg-rojo' : 'text-mg-verde'}`}>{cant(l.cantidad)}</TableCell>
                    <TableCell className="text-right num">{l.costo_unitario === null ? '—' : `$ ${Money.parse(l.costo_unitario).toDisplay()}`}</TableCell>
                    <TableCell className="text-right num">{l.existencia_antes !== null ? `${cant(l.existencia_antes)} → ${cant(l.existencia_despues)}` : 'pendiente'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {a.data.valor_total === null ? null : <p className="text-right text-sm">Valor del ajuste: <b className="num">$ {Money.parse(a.data.valor_total).toDisplay()}</b></p>}
            {a.data.estado === 'pendiente' && aprueba ? (
              <>
                {/* Nadie aprueba lo suyo, salvo la dueña (decisión del 2026-10-27): puede retirarlo con «Rechazar» */}
                {a.data.es_propio && !esDuena ? <p className="rounded-lg border border-mg-ambar/50 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="ajuste-propio">Este ajuste lo propusiste tú (o salió de un conteo que confirmaste): lo aprueba otra persona con el permiso de aprobar ajustes, o la dueña. Puedes retirarlo con «Rechazar».</p> : null}
                {veCosto && vista.data ? <VistaAjuste vista={vista.data} al="aprobar" testid="ajuste-aprobar-vista-libros" /> : null}
                <Campo etiqueta="Comentario" htmlFor="res-com"><Input id="res-com" value={comentario} onChange={(e) => setComentario(e.target.value)} data-testid="ajuste-comentario" /></Campo>
                <DialogFooter>
                  <Button variant="destructive" cargando={resolver.isPending} onClick={() => decidir('rechazar')} data-testid="ajuste-rechazar">Rechazar</Button>
                  {a.data.es_propio && !esDuena ? null : <Button cargando={resolver.isPending} onClick={() => decidir('aprobar')} data-testid="ajuste-aprobar">Aprobar y aplicar</Button>}
                </DialogFooter>
              </>
            ) : null}
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
